import { createContext, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { typeToFlattenedError, z, ZodIssue, ZodObject } from 'zod'

import { checkBoxLiteralValue } from 'containers/forms/Validation'

// eslint-disable-next-line @typescript-eslint/ban-types
export type ValidationMessages<T = {}> = typeToFlattenedError<
  T,
  ZodIssue
> | null

export type ValidationContextProps = {
  messages: ValidationMessages | null
  setMessages: (messages: ValidationMessages) => void
  getError: (name: string, placeholder?: string) => Array<string> | null
}

export const ValidationContext = createContext<ValidationContextProps>({
  messages: null,
  setMessages: () => null,
  getError: () => null,
})

type ValidationProviderProps = {
  children: React.ReactNode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationObject: ZodObject<any>
}

const extractValues = (item: ZodIssue) => {
  const values: Record<string, number | bigint> = {}
  if ('minimum' in item) {
    values.minimum = item.minimum
  }
  if ('maximum' in item) {
    values.maximum = item.maximum
  }
  return values
}

const zodIssueToi18n = (issue: ZodIssue) => {
  const { code } = issue

  if (code === z.ZodIssueCode.invalid_type && issue.received === 'undefined') {
    return 'string_missing'
  } else if (code === z.ZodIssueCode.custom) {
    return issue.message
  } else if (
    code === z.ZodIssueCode.invalid_literal &&
    issue.expected === checkBoxLiteralValue
  ) {
    return 'checkbox_required'
  } else {
    const id = 'validation' in issue ? `${issue.validation}_${code}` : code
    return id
  }
}

export const ValidationProvider = ({
  children,
  validationObject,
}: ValidationProviderProps) => {
  const [validationMessages, setValidationMessages] =
    useState<ValidationMessages | null>(null)
  const { formatMessage } = useIntl()

  const getError = (name: string, placeholder?: string) => {
    const errors = (
      validationMessages as typeToFlattenedError<
        typeof validationObject.shape,
        ZodIssue
      > | null
    )?.fieldErrors

    return (
      errors?.[name]?.map((item) => {
        const id = zodIssueToi18n(item)
        return formatMessage(
          { id },
          {
            name: placeholder ?? name,
            ...extractValues(item),
          }
        )
      }) ?? null
    )
  }

  return (
    <ValidationContext.Provider
      value={{
        messages: validationMessages,
        setMessages: setValidationMessages,
        getError,
      }}
    >
      {children}
    </ValidationContext.Provider>
  )
}

export const useValidationContext = () => useContext(ValidationContext)
